/**
 * FRONT-3171: how the file window shows a file, from its name alone. Pure, so
 * the choice is tested without a browser. Mirrors the server's allowlist
 * (`src/coding/project-file.ts`): only PDFs and raster images arrive as bytes.
 */
export type FileViewKind = 'markdown' | 'pdf' | 'image' | 'text';

export function fileViewKind(path: string): FileViewKind {
  const ext = /\.([A-Za-z0-9]+)$/.exec(path)?.[1]?.toLowerCase() ?? '';
  if (ext === 'md' || ext === 'markdown' || ext === 'mdx') return 'markdown';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'text';
}

const CODE_LANGUAGE: Record<string, string> = {
  js: 'js', mjs: 'js', cjs: 'js', jsx: 'js',
  ts: 'ts', mts: 'ts', cts: 'ts', tsx: 'ts',
  json: 'json', css: 'css', html: 'html', htm: 'html', svg: 'xml', xml: 'xml',
  py: 'py', sh: 'bash', bash: 'bash', zsh: 'bash', yml: 'yaml', yaml: 'yaml', toml: 'toml',
  sql: 'sql', go: 'go', rs: 'rs', java: 'java', c: 'c', h: 'c', lua: 'lua', ini: 'ini', env: 'ini',
  diff: 'diff', patch: 'diff', csv: 'csv', log: 'log',
};

/**
 * The colouring a code file gets (the shared code view's language names), by
 * extension; `plain` for anything unknown, still with line numbers. A
 * Dockerfile or Makefile is known by its whole name.
 */
export function codeLanguage(path: string): string {
  const base = path.split('/').pop() ?? path;
  if (/^dockerfile$/i.test(base)) return 'docker';
  if (/^makefile$/i.test(base)) return 'make';
  const ext = /\.([A-Za-z0-9]+)$/.exec(base)?.[1]?.toLowerCase() ?? '';
  return CODE_LANGUAGE[ext] ?? 'plain';
}

/** JSON reads best indented; anything that does not parse is shown as it is. */
export function prettyIfJson(path: string, text: string): string {
  if (!/\.json$/i.test(path)) return text;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/** The address one project file is read from. */
export function projectFileUrl(sessionId: string, path: string): string {
  return `/api/coding/sessions/${encodeURIComponent(sessionId)}/file?path=${encodeURIComponent(path)}`;
}

/**
 * The file an activity row is about, when there is one to open: what the
 * Builder wrote, read or looked at, and only once that step succeeded.
 */
export function openableFile(item: { tool: string; input: string; result: 'pending' | 'ok' | 'error' }): string | null {
  if (item.result !== 'ok') return null;
  const name = item.tool.replace(/^mcp__[^_]+__/, '');
  if (name !== 'write_file' && name !== 'read_file' && name !== 'view_image') return null;
  try {
    const input = JSON.parse(item.input || '{}') as { path?: unknown };
    return typeof input.path === 'string' && input.path.trim() ? input.path.trim() : null;
  } catch {
    return null;
  }
}

/**
 * A Markdown file's own pictures and links point at files next to it in the
 * project (`![mock-up](shots/home.png)`, `[spec](spec.pdf)`). Shown in Eddy
 * those addresses mean nothing, so each project-relative one is turned into
 * the project file's address — the picture renders in place, the link opens
 * the file. Web, mail, in-page and absolute addresses, and any path that would
 * climb out of the project, are left exactly as written.
 */
export function resolveMarkdownAssets(markdown: string, mdPath: string, sessionId: string): string {
  const dir = mdPath.includes('/') ? mdPath.slice(0, mdPath.lastIndexOf('/')) : '';
  return markdown.replace(/(!?\[[^\]\n]*\]\()([^)\s]+)((?:\s+"[^"]*")?\))/g, (whole, open: string, target: string, close: string) => {
    if (/^(?:[a-z][a-z0-9+.-]*:|\/|#)/i.test(target)) return whole;
    const resolved = joinProjectPath(dir, decodeSafe(target));
    return resolved ? `${open}${projectFileUrl(sessionId, resolved)}${close}` : whole;
  });
}

function decodeSafe(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** `dir` + `rel` inside the project, or null when `..` would leave it. */
function joinProjectPath(dir: string, rel: string): string | null {
  const out: string[] = dir ? dir.split('/') : [];
  for (const part of rel.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (out.length === 0) return null;
      out.pop();
    } else out.push(part);
  }
  return out.length ? out.join('/') : null;
}

/**
 * Where a file window's file comes from: a file in a Build project (by its
 * project path — what "Wrote BRIEF.md" names), or a file the person uploaded
 * and attached to a message (by its Files id).
 */
export type FileSource =
  | { kind: 'project'; sessionId: string; path: string }
  | { kind: 'upload'; fileId: string; name: string; contentType?: string };

export function fileSourceUrl(src: FileSource): string {
  return src.kind === 'project'
    ? projectFileUrl(src.sessionId, src.path)
    : `/api/files/${encodeURIComponent(src.fileId)}/raw`;
}

/** The name the window shows: the file's own name, never its folder. */
export function fileSourceName(src: FileSource): string {
  const p = src.kind === 'project' ? src.path : src.name;
  return p.split('/').pop() || p;
}

/** How to show it — by name, and for an upload without a telling name by its type. */
export function fileSourceViewKind(src: FileSource): FileViewKind {
  const byName = fileViewKind(src.kind === 'project' ? src.path : src.name);
  if (byName !== 'text' || src.kind === 'project') return byName;
  const type = src.contentType ?? '';
  if (type === 'application/pdf') return 'pdf';
  if (/^image\/(png|jpe?g|gif|webp)$/.test(type)) return 'image';
  if (type === 'text/markdown') return 'markdown';
  return 'text';
}

/** The Files id an image's address points at (`/api/files/<id>/raw`), or null. */
export function uploadIdFromImageSrc(src: string | null | undefined): string | null {
  const m = /^\/api\/files\/([^/?#]+)\/raw(?:[?#].*)?$/.exec(src ?? '');
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}
